import React, { useState, useCallback } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import {
  ActiveListItem, DrawerList, DrawerListItem, DrawerText,
}
  from '@fuww/library/src/Drawer/index';
import encodeIcon from '@fuww/library/src/lib/encodeIcon';
import MenuList from '@fuww/library/src/Typography/MenuList';
import { ListItemMeta, CollapsibleList, ListItemText }
  from '@fuww/library/src/List/index';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import IconContainer from '@fuww/library/src/IconContainer';
import OpenInNewIcon from '@fuww/library/src/Icons/OpenInNew';
import messages from '../../lib/messages.json';
import getList from '../../data/drawer';
import { Item } from '../../data/navigation';
import { useSiteContext } from '../SiteContext';
import LinkAndAnchor from '../LinkAndAnchor';

export { ModalDrawer } from '@fuww/library/src/Drawer/index';

const iconExpandLess = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/><path d="M0 0h24v24H0z" fill="none"/></svg>';
const iconExpandMore = '<svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24"><path d="M0 0h24v24H0z" fill="none"/><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"/></svg>';
const expandLess = encodeIcon(iconExpandLess);
const expandMore = encodeIcon(iconExpandMore);

const listItemProperties = {
  name: PropTypes.string,
  id: PropTypes.string.isRequired,
  props: PropTypes.shape({}),
  route: PropTypes.string,
  router: PropTypes.bool,
  closeDrawer: PropTypes.func.isRequired,
  params: PropTypes.shape({}),
  currentRoute: PropTypes.string,
  target: PropTypes.string,
  executive: PropTypes.bool,
};

type ListItemProperties = InferProps<typeof listItemProperties> & {
  href?: string;
  items?: Item[];
  target?: string;
};

const ListItem = ({
  id,
  href: originalHref,
  items,
  props: properties = {},
  route,
  router = false,
  params: parameters = {},
  target,
  closeDrawer,
  executive,
}: ListItemProperties) => {
  const intl = useIntl();
  const hrefKey = `${id}.href`;
  const hrefTranslation = messages[hrefKey] as MessageDescriptor | undefined;

  const href = hrefTranslation
    ? intl.formatMessage(hrefTranslation) : originalHref;

  const [open, setOpen] = useState(false);

  const handleClick = useCallback(() => {
    setOpen(!open);
  }, [open]);

  if (items && items.length > 0) {
    return (
      <>
        <ActiveListItem
          onClick={handleClick}
          tag="div"
          role="button"
          data-testid={`toggle-collapse-${id}`}
          showDivider
        >
          <ListItemText
            primary={(
              <MenuList>
                {intl.formatMessage(messages[id])}
              </MenuList>
              )}
          />
          {open
            ? (
              <ListItemMeta>
                <img src={expandMore} alt="expand more" />
              </ListItemMeta>
            )
            : (
              <ListItemMeta>
                <img src={expandLess} alt="expand less" />
              </ListItemMeta>
            )}
        </ActiveListItem>
        <CollapsibleList
          {...properties}
          in={open}
        >
          {items.map(({
            id: itemId,
            href: originalItemHref,
            router: itemRouter,
            params: itemParameters,
            route: itemRoute,
            target: itemTarget,
            ...itemProperties
          }) => {
            const itemHrefKey = `${itemId}.href`;

            // eslint-disable-next-line max-len
            const itemHrefTranslation = messages[itemHrefKey] as MessageDescriptor | undefined;
            const itemHref = itemHrefTranslation
              ? intl.formatMessage(itemHrefTranslation)
              : originalItemHref;

            return (
              <LinkAndAnchor
                onClick={closeDrawer}
                route={itemRoute}
                useRouter={itemRouter}
                href={itemHref}
                params={itemParameters}
                key={itemId}
                target={itemTarget}
                hoverUnderline
              >
                <DrawerListItem
                  tag="div"
                  showDivider
                  {...itemProperties}
                >
                  <DrawerText>
                    {intl.formatMessage(messages[itemId])}
                  </DrawerText>
                </DrawerListItem>
              </LinkAndAnchor>
            );
          })}
        </CollapsibleList>
      </>
    );
  }

  return (
    <LinkAndAnchor
      onClick={closeDrawer}
      route={route}
      href={href}
      params={parameters}
      useRouter={router}
      target={target}
      hoverUnderline
      executive={executive}
    >
      <ActiveListItem
        tag="div"
        executive={executive ?? undefined}
        showDivider
      >
        <MenuList executive={executive}>
          {intl.formatMessage(messages[id])}
          {target === '_blank' && (
          <IconContainer>
            <OpenInNewIcon
              title={intl.formatMessage(messages.openInNew)}
              fillColor="--black"
              size={12}
            />
          </IconContainer>
          )}
        </MenuList>
      </ActiveListItem>
    </LinkAndAnchor>
  );
};

ListItem.propTypes = listItemProperties;

const drawerContentProperties = {
  closeDrawer: PropTypes.func.isRequired,
};

type DrawerContentProperties = InferProps<typeof drawerContentProperties>;

const DrawerContent = ({ closeDrawer }: DrawerContentProperties) => {
  const intl = useIntl();
  const {
    newsboard, locale, hasNews, hasJobsInLanding, origin,
  } = useSiteContext();
  const { route: currentRoute } = useRouter();

  return (
    <DrawerList tag="nav">
      {
        getList(
          intl,
          newsboard,
          locale,
          hasNews,
          hasJobsInLanding,
          origin,
        )
          .map(({
            id,
            href,
            items,
            route,
            router,
            params,
            target,
            executive,
          }) => (
            <ListItem
              key={id}
              id={id}
              href={href}
              items={items}
              route={route}
              router={router}
              params={params}
              closeDrawer={closeDrawer}
              currentRoute={currentRoute}
              target={target}
              executive={executive}
            />
          ))
      }
    </DrawerList>
  );
};

DrawerContent.propTypes = drawerContentProperties;

export {
  DrawerContent,
};
